import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { REVIEWS, VILLA } from "../data";
import { useLang } from "../i18n";

const ReviewCard = ({ review, center, t }) => {
  const [expanded, setExpanded] = useState(false);
  const isLong = review.text.length > 260;
  return (
    <figure
      className={`bg-sand-bg border border-[var(--line)] p-8 md:p-12 flex flex-col h-full transition-opacity ${
        center ? "" : "pointer-events-none"
      }`}
    >
      <figcaption className="text-ink font-medium text-sm mb-3">{review.name}</figcaption>
      <div className="flex gap-1 mb-6">
        {[...Array(review.rating)].map((_, s) => (
          <Star key={s} className="w-3.5 h-3.5 fill-terracotta text-terracotta" />
        ))}
      </div>
      <blockquote
        className={`font-serif font-light text-base md:text-lg leading-relaxed tracking-tight ${
          expanded ? "" : "line-clamp-5"
        }`}
        data-testid="review-text"
      >
        “{review.text}”
      </blockquote>
      {isLong && (
        <button
          onClick={() => setExpanded((v) => !v)}
          data-testid="review-read-more"
          className="mt-3 mb-6 self-start text-xs tracking-wide text-terracotta link-underline"
        >
          {expanded ? t.reviews.readLess : t.reviews.readMore}
        </button>
      )}
      <div className="flex items-center justify-end border-t border-[var(--line)] pt-5 mt-auto">
        <span className="text-ink-soft text-xs">{t.reviews.badge}</span>
      </div>
    </figure>
  );
};

export const Reviews = () => {
  const { t } = useLang();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = REVIEWS.length;

  const next = useCallback(() => setIdx((i) => (i + 1) % n), [n]);
  const prev = () => setIdx((i) => (i - 1 + n) % n);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next, paused]);

  const at = (o) => REVIEWS[(idx + o + n) % n];
  const slots = [
    { offset: -1, pos: "left" },
    { offset: 0, pos: "center" },
    { offset: 1, pos: "right" },
  ];

  return (
    <section id="avis" className="px-6 md:px-12 py-24 md:py-36 max-w-[1600px] mx-auto" data-testid="reviews-section">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16 md:mb-24">
        <div>
          <span className="text-[11px] tracking-[0.35em] uppercase text-terracotta">{t.reviews.eyebrow}</span>
          <h2 className="font-serif font-light text-4xl md:text-6xl mt-4 tracking-tight">{t.reviews.title}</h2>
        </div>
        <div className="flex items-center gap-3" data-testid="google-rating">
          <div className="flex gap-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-4 h-4 fill-terracotta text-terracotta" />
            ))}
          </div>
          <span className="text-ink-soft text-sm">
            <span className="font-serif text-2xl text-ink mr-1">5,0</span> {t.reviews.googleLabel}
          </span>
        </div>
      </div>

      <div
        className="relative"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        data-testid="reviews-carousel"
      >
        <div className="grid md:grid-cols-3 gap-4 md:gap-6 items-stretch">
          {slots.map(({ offset, pos }) => {
            const review = at(offset);
            const center = offset === 0;
            return (
              <div key={pos} className={center ? "" : "hidden md:block"} data-testid={`review-slide-${pos}`}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`${review.name}-${idx}`}
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: center ? 1 : 0.35, x: 0, scale: center ? 1 : 0.96 }}
                    exit={{ opacity: 0, x: -40 }}
                    transition={{ duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
                    className="h-full"
                  >
                    <ReviewCard review={review} center={center} t={t} />
                  </motion.div>
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        <div className="mt-8 flex items-center justify-center gap-6">
          <button
            onClick={prev}
            data-testid="reviews-prev"
            aria-label="Avis précédent"
            className="w-11 h-11 border border-[var(--line)] flex items-center justify-center hover:border-terracotta hover:text-terracotta transition-colors"
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <div className="flex gap-2" data-testid="reviews-dots">
            {REVIEWS.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`Avis ${i + 1}`}
                data-testid={`reviews-dot-${i}`}
                className={`h-1.5 transition-all duration-500 ${
                  i === idx ? "w-8 bg-terracotta" : "w-1.5 bg-[var(--line)] hover:bg-ink-soft"
                }`}
              />
            ))}
          </div>
          <button
            onClick={next}
            data-testid="reviews-next"
            aria-label="Avis suivant"
            className="w-11 h-11 border border-[var(--line)] flex items-center justify-center hover:border-terracotta hover:text-terracotta transition-colors"
          >
            <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <div className="mt-10 text-center">
        <a
          href={VILLA.mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="all-google-reviews-link"
          className="text-sm text-ink-soft link-underline hover:text-ink"
        >
          {t.reviews.allLink}
        </a>
      </div>
    </section>
  );
};
